import {
  forEachSchemaNode,
  IForEachSchemaOptions,
  Schema,
  yup,
} from '@framework/validation';
import { Decimal } from 'decimal.js-light';
import { groupBy } from 'lodash';
import { WidgetTypeInternalMetaKey } from '../../../../../../validator/form-builder/setWidgetTypeMeta';
import { WidgetType } from '../../../../Widget';
import { PaymentRowFieldKey } from '../createPaymentRowFormSchema';

export interface IPaymentRowData {
  widgetPath: string;
  idSezione: string;

  descrizione: string;
  importoUnitarioListino: string;
  quantita: number;
  unitaMisura: string;
  iva: number;
  sconto: number;

  importoUnitario: string;
  importoTotaleListino: string;
  importoTotale: string;
  importoImponibile: string;
  importoIva: string;
}

export interface IPaymentInstanceReport {
  totale: string;
  totaleNoSconto: string;
  imponibile: string;
  iva: string;
}

export interface IPaymentIntanceData {
  riepilogo: IPaymentInstanceReport;
  voci: IPaymentRowData[];
  idSezione: string;
}

/**
 * Recupera il path al widget e il campo associato.
 * Il path è nella forma <id-sezione>.<path-padre?>.<widget-code>@<field>,
 * tranne per il "totale", che è nella forma <id-sezione>.<path-padre?>.<widget-code>.
 *
 * La gestione è complessa, ma così evitiamo problemi con path padre che contengono "@" a loro volta.
 */
export function getNodeCodes(code: string): {
  widgetPath: string;
  field: PaymentRowFieldKey;
} {
  // Divido per tutte le key
  const splitted = code.split('.');

  // Prendo l'ultima (quella del widget voce di costo)
  const last = splitted[splitted.length - 1];

  if (!last.includes('@')) {
    return {
      widgetPath: code,
      field: 'totalAmount' satisfies PaymentRowFieldKey,
    };
  }

  // Estraggo il field. Se non è presente si tratta del totale
  const field = last.split('@')[1] as PaymentRowFieldKey;

  // Estraggo il path (tutto tranne il field)
  const path = code.split('@').slice(0, -1).join('@');

  return {
    widgetPath: path,
    field,
  };
}

function toDecimal(value: any): Decimal {
  if (value == null) {
    return new Decimal(0);
  }

  return new Decimal(value);
}

/**
 * Funzione utilizzata nel validatore per estrarre tutte le
 * istanze attive di Widget Voce di Costo di una sezione dati,
 * completandole con il riepilogo totale (imponibile, iva, totale).
 */
export function extractPaymentInstance(
  schema: yup.ISchema<any>,
  options: IForEachSchemaOptions,
): IPaymentIntanceData[] {
  const paymentRowNodes: Array<{
    widgetPath: string;
    field: PaymentRowFieldKey;
    value: any;
  }> = [];

  // Recuperiamo tutti i campi delle voci di costo
  forEachSchemaNode(schema, options, node => {
    if (
      node.schema instanceof Schema &&
      node.schema.meta?.()?.[WidgetTypeInternalMetaKey] ===
        WidgetType.PaymentRow &&
      !node.schema.spec.strip
    ) {
      const code = node.path as string | undefined;
      if (!code) {
        return;
      }

      const { widgetPath, field } = getNodeCodes(code);

      paymentRowNodes.push({
        widgetPath,
        field: field,
        value: node.value,
      });
    }
  });

  // Raggruppiamo le voci di costo per widget (identificato dal path)
  const grouped = groupBy(paymentRowNodes, 'widgetPath');

  // Creiamo un array di oggetti con i campi delle voci di costo
  const reduced: Array<
    Record<PaymentRowFieldKey | 'widgetPath' | 'sectionId', any>
  > = [];
  for (const widgetPath in grouped) {
    const sectionId = widgetPath.split('.')[0];
    const rows = grouped[widgetPath];
    reduced.push(
      rows.reduce(
        (acc, row) => {
          acc[row.field] = row.value;
          return acc;
        },
        { widgetPath, sectionId } as Record<
          PaymentRowFieldKey | 'widgetPath' | 'sectionId',
          any
        >,
      ),
    );
  }

  // Mappiamo i campi per ottenere l'oggetto finale
  const paymentRows: IPaymentRowData[] = reduced
    // rimuovo le voci non incluse
    .filter(r => r.included)
    .map(
      row =>
        ({
          widgetPath: row.widgetPath,
          idSezione: row.sectionId,

          descrizione: row.description,
          importoUnitarioListino: row.baseUnitAmount,
          quantita: row.quantity,
          unitaMisura: row.unit,
          iva: row.vat,
          sconto: row.discount,

          importoUnitario: row.unitAmount,
          importoTotale: row.totalAmount,
          importoTotaleListino: row.totalNoDiscountAmount,
          importoImponibile: row.taxableAmount,
          importoIva: row.vatAmount,
        } satisfies IPaymentRowData),
    );

  const groupedPaymentRows = groupBy(paymentRows, 'idSezione');

  const result: IPaymentIntanceData[] = [];

  for (const sectionId in groupedPaymentRows) {
    const paymentRows = groupedPaymentRows[sectionId];
    const report = {
      totale: paymentRows
        .reduce(
          (acc, row) => acc.add(toDecimal(row.importoTotale)),
          new Decimal(0),
        )
        .toString(),
      totaleNoSconto: paymentRows
        .reduce(
          (acc, row) => acc.add(toDecimal(row.importoTotaleListino)),
          new Decimal(0),
        )
        .toString(),
      imponibile: paymentRows
        .reduce(
          (acc, row) => acc.add(toDecimal(row.importoImponibile)),
          new Decimal(0),
        )
        .toString(),
      iva: paymentRows
        .reduce(
          (acc, row) => acc.add(toDecimal(row.importoIva)),
          new Decimal(0),
        )
        .toString(),
    };

    result.push({ idSezione: sectionId, riepilogo: report, voci: paymentRows });
  }

  return result;
}
